import { Field } from 'formik';
import InputMask from 'react-input-mask';
import { Box, Typography, TextField, MenuItem, Grid } from '@mui/material';

interface DynamicField {
  id: string;
  label: string;
  field_type: string;
  required: boolean;
  options?: string[];
  placeholder?: string;
}

interface PersonalInfoFormProps {
  values: any;
  errors: any;
  touched: any;
  setFieldValue: (field: string, value: any) => void;
  isSubmitting: boolean;
  dynamicFields: DynamicField[];
  fieldPrefix?: string;
  title?: string;
  subtitle?: string;
  showHeader?: boolean;
}

export function PersonalInfoForm({
  values,
  errors,
  touched,
  setFieldValue,
  isSubmitting,
  dynamicFields,
  fieldPrefix,
  title = 'Complete sua inscrição',
  subtitle = 'Utilizaremos estas informações apenas para a sua inscrição, comunicação do evento e confirmação de pagamento.',
  showHeader = true,
}: PersonalInfoFormProps) {
  const fieldName = (name: string) => (fieldPrefix ? `${fieldPrefix}.${name}` : name);
  // Estilos para labels acima dos inputs (mobile e desktop)
  const labelStyles = {
    fontSize: { xs: '0.875rem', sm: '1rem' },
    fontFamily: 'Manrope, sans-serif',
    position: 'static !important',
    transform: 'none !important',
    width: '100%',
    marginBottom: '8px',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    overflow: 'visible',
    textOverflow: 'clip',
    maxWidth: '100%',
    lineHeight: 1.4,
    '&.MuiInputLabel-shrink': {
      position: 'static !important',
      transform: 'none !important',
      whiteSpace: 'normal',
      wordBreak: 'break-word',
      overflowWrap: 'break-word',
    },
  };

  const fieldSxStyles = {
    '& .MuiOutlinedInput-root': {
      fontFamily: 'Manrope, sans-serif',
    },
    '& .MuiInputLabel-root': {
      fontFamily: 'Manrope, sans-serif',
    },
    '& .MuiFormControl-root': {
      display: 'flex',
      flexDirection: 'column',
    },
    '& .MuiInputBase-root': {
      marginTop: 0,
    },
  };

  return (
    <Box>
      {showHeader && (
        <>
          <Typography variant="h6" fontWeight={600} gutterBottom sx={{ color: '#424242', fontSize: '1.125rem', fontFamily: 'Manrope, sans-serif' }}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6, color: '#757575', fontSize: '0.875rem', fontFamily: 'Manrope, sans-serif' }}>
            {subtitle}
          </Typography>
        </>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Field
          as={TextField}
          fullWidth
          label="Nome*"
          name={fieldName('name')}
          required
          error={!!(touched.name && errors.name)}
          helperText={touched.name && errors.name}
          InputLabelProps={{
            shrink: true,
            sx: labelStyles,
          }}
          sx={fieldSxStyles}
        />

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <InputMask
              mask="999.999.999-99"
              value={values.cpf}
              onChange={(e: any) => setFieldValue(fieldName('cpf'), e.target.value)}
              disabled={isSubmitting}
            >
              {(inputProps: any) => (
                <TextField
                  {...inputProps}
                  fullWidth
                  label="CPF*"
                  name={fieldName('cpf')}
                  required
                  error={!!(touched.cpf && errors.cpf)}
                  helperText={touched.cpf && errors.cpf}
                  InputLabelProps={{
                    shrink: true,
                    sx: labelStyles,
                  }}
                  sx={fieldSxStyles}
                />
              )}
            </InputMask>
          </Grid>
          <Grid item xs={12} sm={6}>
            <InputMask
              mask="(99) 99999-9999"
              value={values.phone}
              onChange={(e: any) => setFieldValue(fieldName('phone'), e.target.value)}
              disabled={isSubmitting}
            >
              {(inputProps: any) => (
                <TextField
                  {...inputProps}
                  fullWidth
                  label="Telefone/Whatsapp*"
                  name={fieldName('phone')}
                  required
                  error={!!(touched.phone && errors.phone)}
                  helperText={touched.phone && errors.phone}
                  InputLabelProps={{
                    shrink: true,
                    sx: labelStyles,
                  }}
                  sx={fieldSxStyles}
                />
              )}
            </InputMask>
          </Grid>
        </Grid>

        <Field
          as={TextField}
          fullWidth
          label="E-mail"
          name={fieldName('email')}
          type="email"
          required
          error={!!(touched.email && errors.email)}
          helperText={touched.email && errors.email}
          InputLabelProps={{
            shrink: true,
            sx: labelStyles,
          }}
          sx={fieldSxStyles}
        />

        {/* Campos dinâmicos */}
        {dynamicFields.map((field) => {
          switch (field.field_type) {
            case 'yes_no':
              return (
                <Field
                  key={field.id}
                  as={TextField}
                  select
                  fullWidth
                  label={field.label}
                  name={fieldName(field.id)}
                  required={field.required}
                  error={!!(touched[field.id] && errors[field.id])}
                  helperText={touched[field.id] && errors[field.id]}
                  InputLabelProps={{
                    shrink: true,
                    sx: labelStyles,
                  }}
                  sx={fieldSxStyles}
                >
                  <MenuItem value="Sim" sx={{ fontFamily: 'Manrope, sans-serif' }}>Sim</MenuItem>
                  <MenuItem value="Não" sx={{ fontFamily: 'Manrope, sans-serif' }}>Não</MenuItem>
                </Field>
              );
            case 'date':
              return (
                <Field
                  key={field.id}
                  as={TextField}
                  fullWidth
                  type="date"
                  label={field.label}
                  name={fieldName(field.id)}
                  required={field.required}
                  placeholder={field.placeholder}
                  InputLabelProps={{
                    shrink: true,
                    sx: labelStyles,
                  }}
                  error={!!(touched[field.id] && errors[field.id])}
                  helperText={touched[field.id] && errors[field.id]}
                  sx={fieldSxStyles}
                />
              );
            case 'number':
              return (
                <Field
                  key={field.id}
                  as={TextField}
                  fullWidth
                  type="number"
                  label={field.label}
                  name={fieldName(field.id)}
                  required={field.required}
                  placeholder={field.placeholder}
                  InputLabelProps={{
                    shrink: true,
                    sx: labelStyles,
                  }}
                  error={!!(touched[field.id] && errors[field.id])}
                  helperText={touched[field.id] && errors[field.id]}
                  sx={fieldSxStyles}
                />
              );
            case 'email':
              return (
                <Field
                  key={field.id}
                  as={TextField}
                  fullWidth
                  type="email"
                  label={field.label}
                  name={fieldName(field.id)}
                  required={field.required}
                  placeholder={field.placeholder}
                  InputLabelProps={{
                    shrink: true,
                    sx: labelStyles,
                  }}
                  error={!!(touched[field.id] && errors[field.id])}
                  helperText={touched[field.id] && errors[field.id]}
                  sx={fieldSxStyles}
                />
              );
            case 'phone':
              return (
                <Field
                  key={field.id}
                  as={TextField}
                  fullWidth
                  label={field.label}
                  name={fieldName(field.id)}
                  required={field.required}
                  placeholder={field.placeholder}
                  InputLabelProps={{
                    shrink: true,
                    sx: labelStyles,
                  }}
                  error={!!(touched[field.id] && errors[field.id])}
                  helperText={touched[field.id] && errors[field.id]}
                  sx={fieldSxStyles}
                />
              );
            case 'select':
              return (
                <Field
                  key={field.id}
                  as={TextField}
                  select
                  fullWidth
                  label={field.label}
                  name={fieldName(field.id)}
                  required={field.required}
                  InputLabelProps={{
                    shrink: true,
                    sx: labelStyles,
                  }}
                  error={!!(touched[field.id] && errors[field.id])}
                  helperText={touched[field.id] && errors[field.id]}
                  sx={fieldSxStyles}
                >
                  {(field.options || []).map((opt: string) => (
                    <MenuItem key={opt} value={opt} sx={{ fontFamily: 'Manrope, sans-serif' }}>
                      {opt}
                    </MenuItem>
                  ))}
                </Field>
              );
            default:
              return (
                <Field
                  key={field.id}
                  as={TextField}
                  fullWidth
                  label={field.label}
                  name={fieldName(field.id)}
                  required={field.required}
                  placeholder={field.placeholder}
                  InputLabelProps={{
                    shrink: true,
                    sx: labelStyles,
                  }}
                  error={!!(touched[field.id] && errors[field.id])}
                  helperText={touched[field.id] && errors[field.id]}
                  sx={fieldSxStyles}
                />
              );
          }
        })}
      </Box>
    </Box>
  );
}

